import { useQuery } from '@tanstack/react-query'
import { SpinnerWithBackground, Text } from '@viapeople/components'
import { useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { trpc } from '../../../utils/trpc'
import { filterComponentsByRole, getUniquePermissionTemplateIds } from '../helpers/previewPermissions'
import { useFormBuilderStore } from '../store'
import type { FilteredComponent, PreviewRoleType } from '../types/preview'
import { PreviewComponentRenderer } from './PreviewComponentRenderer'

type FormValues = Record<string, string | number | boolean>

interface FormPreviewRendererProps {
	selectedRole: PreviewRoleType
}

export function FormPreviewRenderer({ selectedRole }: FormPreviewRendererProps) {
	const { organizationId } = useParams<{ organizationId: string }>()
	const formComponents = useFormBuilderStore((state) => state.formComponents)

	// Get unique permission template IDs from components
	const permissionTemplateIds = useMemo(() => getUniquePermissionTemplateIds(formComponents), [formComponents])

	// Fetch permission templates
	const permissionTemplatesQuery = useQuery({
		...trpc.admin.permissionTemplates.search.queryOptions({
			organizationId: organizationId as string,
			search: '',
			perPage: 1000,
			page: 1,
		}),
		enabled: !!organizationId && permissionTemplateIds.size > 0,
	})

	// Filter components based on selected role
	const filteredComponents = useMemo<FilteredComponent[]>(() => {
		if (!permissionTemplatesQuery.data?.data) {
			// If no templates loaded yet, show all components with full permissions
			return formComponents
				.filter((c) => c.action !== 'delete')
				.map((c) => ({
					...c,
					read: true,
					write: true,
					reports: false,
					optional: false,
					reportOnly: false,
					children: c.children?.map((child) => ({
						...child,
						read: true,
						write: true,
						reports: false,
						optional: false,
						reportOnly: false,
					})),
				})) as FilteredComponent[]
		}

		return filterComponentsByRole(formComponents, permissionTemplatesQuery.data.data, selectedRole)
	}, [formComponents, permissionTemplatesQuery.data, selectedRole])

	// Setup form for interactive preview
	const methods = useForm<FormValues>({
		defaultValues: {},
	})

	if (permissionTemplatesQuery.isLoading) {
		return (
			<div className='flex items-center justify-center h-64'>
				<SpinnerWithBackground />
			</div>
		)
	}

	if (formComponents.filter((c) => c.action !== 'delete').length === 0) {
		return (
			<div className='flex flex-col items-center justify-center h-64 text-center'>
				<Text variant='medium' className='text-neutral-500'>
					No components to preview
				</Text>
				<Text variant='small' className='text-neutral-400 mt-2'>
					Add components to the form to see them in preview
				</Text>
			</div>
		)
	}

	if (filteredComponents.length === 0) {
		return (
			<div className='flex flex-col items-center justify-center h-64 text-center'>
				<Text variant='medium' className='text-neutral-500'>
					No visible components for this role
				</Text>
				<Text variant='small' className='text-neutral-400 mt-2'>
					The selected role does not have permission to view any components in this form.
					<br />
					Check the permission templates to configure visibility.
				</Text>
			</div>
		)
	}

	return (
		<FormProvider {...methods}>
			<form className='space-y-6'>
				{filteredComponents.map((component) => (
					<PreviewComponentRenderer
						key={component.id}
						component={component}
						control={methods.control}
						allComponents={filteredComponents}
					/>
				))}
			</form>
		</FormProvider>
	)
}
